import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { FileText, Plus, Eye, Printer, Download, Mail, MessageSquare, MoreHorizontal, FilePlus, Trash2, Upload, FolderOpen, Edit, AlertTriangle, ClipboardList, CheckCircle, Loader2, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { DragDropUploader, AttachmentsList } from '../components/FileUpload';
import { toast } from 'sonner';

// ICRCL Details
const ICRCL_NAME = "Innovative Care and Rehab Centre Limited (ICRCL)";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

export default function EvaluationsPage() {
  const { api, user } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  
  // Super Admin check
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  
  // Entry mode - upload document OR fill form
  const [entryMode, setEntryMode] = useState('form'); // 'form' or 'upload'
  const [uploadedEvalFile, setUploadedEvalFile] = useState(null);
  const [uploadingEval, setUploadingEval] = useState(false);
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'custom',
    description: '',
    sections: [{ title: 'Section 1', fields: [{ name: '', type: 'textarea', required: false }] }]
  });
  
  // Form state
  const [formData, setFormData] = useState({
    patient_id: '',
    report_type: '',
    date: new Date().toISOString().split('T')[0],
    // Common fields
    child_name: '',
    therapy: '',
    dob: '',
    sex: '',
    // OT specific fields
    purpose: '',
    behaviour: '',
    sensory: '',
    neuromusculoskeletal: '',
    reflex: '',
    muscle_strength: '',
    muscle_tone: '',
    gross_motor: '',
    fine_motor: '',
    praxis: '',
    cognition_perception: '',
    academic_skills: '',
    problem_solving: '',
    socialization: '',
    play: '',
    adl_performance: '',
    advice_home: '',
    advice_school: '',
    // SLT specific fields
    attention_listening: '',
    comprehension: '',
    expression: '',
    speech_sound: '',
    oral_motor: '',
    pragmatic: '',
    advice_plan: '',
    // Therapist info
    therapist_name: '',
    therapist_designation: '',
    therapist_organization: ICRCL_NAME
  });

  useEffect(() => {
    fetchData();
    fetchCustomTemplates();
  }, []);

  const fetchData = async () => {
    try {
      const [evaluationsRes, patientsRes] = await Promise.all([
        api.get('/evaluations'),
        api.get('/patients')
      ]);
      setEvaluations(evaluationsRes.data);
      setPatients(patientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load evaluation reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await api.get('/evaluations/templates/custom/list');
      setCustomTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch custom templates:', error);
    }
  };

  // Template management functions
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/evaluations/templates/custom', templateForm);
      toast.success('Evaluation report template created successfully');
      setTemplateDialogOpen(false);
      setTemplateForm({
        name: '',
        type: 'custom',
        description: '',
        sections: [{ title: 'Section 1', fields: [{ name: '', type: 'textarea', required: false }] }]
      });
      fetchCustomTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const addSection = () => {
    setTemplateForm({
      ...templateForm,
      sections: [...templateForm.sections, { title: `Section ${templateForm.sections.length + 1}`, fields: [{ name: '', type: 'textarea', required: false }] }]
    });
  };

  const addField = (sectionIndex) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].fields.push({ name: '', type: 'textarea', required: false });
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const updateSection = (sectionIndex, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const updateField = (sectionIndex, fieldIndex, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].fields[fieldIndex][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const removeSection = (sectionIndex) => {
    if (templateForm.sections.length > 1) {
      const newSections = templateForm.sections.filter((_, i) => i !== sectionIndex);
      setTemplateForm({ ...templateForm, sections: newSections });
    }
  };

  const removeField = (sectionIndex, fieldIndex) => {
    const newSections = [...templateForm.sections];
    if (newSections[sectionIndex].fields.length > 1) {
      newSections[sectionIndex].fields = newSections[sectionIndex].fields.filter((_, i) => i !== fieldIndex);
      setTemplateForm({ ...templateForm, sections: newSections });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api.delete(`/evaluations/templates/custom/${templateId}`);
      toast.success('Template deleted');
      fetchCustomTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patient_id: patientId,
        child_name: `${patient.first_name} ${patient.last_name}`,
        dob: patient.date_of_birth,
        sex: patient.sex
      });
    }
  };

  const handleReportTypeSelect = (type) => {
    setFormData({
      ...formData,
      report_type: type,
      therapy: type === 'ot_evaluation' ? 'Occupational Therapy' : 'Speech and Language Therapy',
      therapist_name: `${user.first_name} ${user.last_name}`,
      therapist_designation: type === 'ot_evaluation' ? 'Occupational Therapist' : 'Speech and Language Therapist'
    });
  };

  // Handle evaluation file selection for upload mode
  const handleEvalFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setUploadedEvalFile(file);
    }
  };

  // Submit evaluation with uploaded document
  const handleUploadEvalSubmit = async () => {
    if (!formData.patient_id || !formData.report_type || !uploadedEvalFile) {
      toast.error('Please select patient, evaluation type, and upload a document');
      return;
    }

    setUploadingEval(true);
    try {
      // Create evaluation record
      const evalRes = await api.post('/evaluations', {
        patient_id: formData.patient_id,
        report_type: formData.report_type,
        date: formData.date,
        findings: {},
        status: 'final',
        has_uploaded_document: true,
        therapist_id: user.id
      });

      const evalId = evalRes.data.id;

      // Upload the document
      const uploadFormData = new FormData();
      uploadFormData.append('file', uploadedEvalFile);
      uploadFormData.append('entity_type', 'evaluation');
      uploadFormData.append('entity_id', evalId);
      uploadFormData.append('category', 'evaluation_document');
      uploadFormData.append('description', `${formData.report_type === 'ot_evaluation' ? 'OT' : 'SLT'} Evaluation - Uploaded Document`);

      await api.post('/uploads', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Evaluation created with uploaded document - marked as COMPLETE');
      setDialogOpen(false);
      setUploadedEvalFile(null);
      setEntryMode('form');
      resetForm();
      fetchEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create evaluation');
    } finally {
      setUploadingEval(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If editing, call update instead
    if (editingReport) {
      return handleUpdate(e);
    }
    
    try {
      await api.post('/evaluations', {
        patient_id: formData.patient_id,
        report_type: formData.report_type,
        therapist_id: user.id,
        date: formData.date,
        findings: formData,
        advice_plan: {
          home: formData.advice_home,
          school: formData.advice_school,
          general: formData.advice_plan
        },
        status: 'draft'
      });
      toast.success('Evaluation report saved successfully');
      setDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save report');
    }
  };

  const resetForm = () => {
    setEditingReport(null);
    setFormData({
      patient_id: '',
      report_type: '',
      date: new Date().toISOString().split('T')[0],
      child_name: '',
      therapy: '',
      dob: '',
      sex: '',
      purpose: '',
      behaviour: '',
      sensory: '',
      neuromusculoskeletal: '',
      reflex: '',
      muscle_strength: '',
      muscle_tone: '',
      gross_motor: '',
      fine_motor: '',
      praxis: '',
      cognition_perception: '',
      academic_skills: '',
      problem_solving: '',
      socialization: '',
      play: '',
      adl_performance: '',
      advice_home: '',
      advice_school: '',
      attention_listening: '',
      comprehension: '',
      expression: '',
      speech_sound: '',
      oral_motor: '',
      pragmatic: '',
      advice_plan: '',
      therapist_name: '',
      therapist_designation: '',
      therapist_organization: ICRCL_NAME
    });
  };

  // Edit evaluation - populate form with existing data
  const handleEdit = (report) => {
    const findings = report.findings || {};
    setEditingReport(report);
    setFormData({
      patient_id: report.patient_id,
      report_type: report.report_type,
      date: report.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      child_name: findings.child_name || report.patient_name || '',
      therapy: findings.therapy || '',
      dob: findings.dob || '',
      sex: findings.sex || '',
      purpose: findings.purpose || '',
      behaviour: findings.behaviour || '',
      sensory: findings.sensory || '',
      neuromusculoskeletal: findings.neuromusculoskeletal || '',
      reflex: findings.reflex || '',
      muscle_strength: findings.muscle_strength || '',
      muscle_tone: findings.muscle_tone || '',
      gross_motor: findings.gross_motor || '',
      fine_motor: findings.fine_motor || '',
      praxis: findings.praxis || '',
      cognition_perception: findings.cognition_perception || '',
      academic_skills: findings.academic_skills || '',
      problem_solving: findings.problem_solving || '',
      socialization: findings.socialization || '',
      play: findings.play || '',
      adl_performance: findings.adl_performance || '',
      advice_home: findings.advice_home || report.advice_plan?.home || '',
      advice_school: findings.advice_school || report.advice_plan?.school || '',
      attention_listening: findings.attention_listening || '',
      comprehension: findings.comprehension || '',
      expression: findings.expression || '',
      speech_sound: findings.speech_sound || '',
      oral_motor: findings.oral_motor || '',
      pragmatic: findings.pragmatic || '',
      advice_plan: findings.advice_plan || report.advice_plan?.general || '',
      therapist_name: findings.therapist_name || '',
      therapist_designation: findings.therapist_designation || '',
      therapist_organization: findings.therapist_organization || ICRCL_NAME
    });
    setDialogOpen(true);
  };

  // Update existing evaluation
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingReport) return;
    
    try {
      await api.put(`/evaluations/${editingReport.id}`, {
        patient_id: formData.patient_id,
        report_type: formData.report_type,
        therapist_id: user.id,
        date: formData.date,
        findings: formData,
        advice_plan: {
          home: formData.advice_home,
          school: formData.advice_school,
          general: formData.advice_plan
        },
        status: editingReport.status || 'draft'
      });
      toast.success('Evaluation report updated successfully');
      setDialogOpen(false);
      setEditingReport(null);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update report');
    }
  };

  // Delete evaluation
  const handleDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await api.delete(`/evaluations/${reportToDelete.id}`);
      toast.success('Evaluation report deleted successfully');
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete report');
    }
  };

  // Check if user can edit this report
  const canEditReport = (report) => {
    if (isSuperAdmin) return true;
    return report.therapist_id === user?.id;
  };

  const handlePrint = (report) => {
    const findings = report.findings || {};
    const isOT = report.report_type === 'ot_evaluation';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${isOT ? 'OT' : 'SLT'} Evaluation Report - ${report.patient_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 20px; margin-bottom: 30px; }
            .logo { font-size: 24px; font-weight: bold; color: #1e3a5f; }
            .org-info { font-size: 12px; color: #666; margin-top: 10px; }
            .patient-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; background: #f8fafc; padding: 15px; margin-bottom: 20px; border-radius: 8px; }
            .patient-info div { font-size: 14px; }
            .patient-info strong { color: #1e3a5f; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; color: #1e3a5f; font-size: 16px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; color: #374151; }
            .field-value { margin-left: 10px; }
            .signature { margin-top: 50px; border-top: 1px solid #e2e8f0; padding-top: 20px; }
            .signature-line { border-bottom: 1px solid #000; width: 200px; margin-bottom: 5px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">ICRCL</div>
            <div class="org-info">
              ${ICRCL_NAME}<br>
              ${ICRCL_ADDRESS}<br>
              Tel: ${ICRCL_PHONE} | Email: ${ICRCL_EMAIL}
            </div>
          </div>
          
          <h2 style="text-align: center; color: #1e3a5f;">${isOT ? 'OCCUPATIONAL THERAPY' : 'SPEECH AND LANGUAGE THERAPY'} EVALUATION REPORT</h2>
          
          <div class="patient-info">
            <div><strong>CHILD'S NAME:</strong> ${findings.child_name || report.patient_name}</div>
            <div><strong>THERAPY:</strong> ${findings.therapy || (isOT ? 'Occupational Therapy' : 'Speech and Language Therapy')}</div>
            <div><strong>DATE:</strong> ${new Date(report.date).toLocaleDateString('en-GB')}</div>
            <div><strong>DOB:</strong> ${findings.dob ? new Date(findings.dob).toLocaleDateString('en-GB') : ''}</div>
            <div><strong>SEX:</strong> ${findings.sex || ''}</div>
            <div></div>
          </div>
          
          ${isOT ? `
            <div class="section">
              <div class="section-title">Purpose:</div>
              <p>${findings.purpose || ''}</p>
            </div>
            
            <div class="section">
              <div class="section-title">FINDINGS:</div>
              
              <div class="field"><span class="field-label">Behaviour:</span><span class="field-value">${findings.behaviour || ''}</span></div>
              <div class="field"><span class="field-label">Sensory:</span><span class="field-value">${findings.sensory || ''}</span></div>
              <div class="field"><span class="field-label">Neuromusculoskeletal:</span><span class="field-value">${findings.neuromusculoskeletal || ''}</span></div>
              <div class="field"><span class="field-label">Reflex:</span><span class="field-value">${findings.reflex || ''}</span></div>
              <div class="field"><span class="field-label">Muscle Strength:</span><span class="field-value">${findings.muscle_strength || ''}</span></div>
              <div class="field"><span class="field-label">Muscle Tone:</span><span class="field-value">${findings.muscle_tone || ''}</span></div>
              <div class="field"><span class="field-label">Gross Motor:</span><span class="field-value">${findings.gross_motor || ''}</span></div>
              <div class="field"><span class="field-label">Fine Motor:</span><span class="field-value">${findings.fine_motor || ''}</span></div>
              <div class="field"><span class="field-label">Praxis:</span><span class="field-value">${findings.praxis || ''}</span></div>
              <div class="field"><span class="field-label">Cognition and Perception:</span><span class="field-value">${findings.cognition_perception || ''}</span></div>
              <div class="field"><span class="field-label">Academic Skills:</span><span class="field-value">${findings.academic_skills || ''}</span></div>
              <div class="field"><span class="field-label">Problem Solving Skills:</span><span class="field-value">${findings.problem_solving || ''}</span></div>
              <div class="field"><span class="field-label">Socialization:</span><span class="field-value">${findings.socialization || ''}</span></div>
              <div class="field"><span class="field-label">Play:</span><span class="field-value">${findings.play || ''}</span></div>
              <div class="field"><span class="field-label">ADL Performance:</span><span class="field-value">${findings.adl_performance || ''}</span></div>
            </div>
            
            <div class="section">
              <div class="section-title">ADVICE / PLAN:</div>
              <div class="field"><span class="field-label">At Home:</span><span class="field-value">${findings.advice_home || ''}</span></div>
              <div class="field"><span class="field-label">At School:</span><span class="field-value">${findings.advice_school || ''}</span></div>
            </div>
          ` : `
            <div class="section">
              <div class="section-title">FINDINGS:</div>
              
              <div class="field"><span class="field-label">Attention and Listening:</span><span class="field-value">${findings.attention_listening || ''}</span></div>
              <div class="field"><span class="field-label">Comprehension (Receptive Language):</span><span class="field-value">${findings.comprehension || ''}</span></div>
              <div class="field"><span class="field-label">Expression (Expressive Language):</span><span class="field-value">${findings.expression || ''}</span></div>
              <div class="field"><span class="field-label">Speech Sound Production / Articulation:</span><span class="field-value">${findings.speech_sound || ''}</span></div>
              <div class="field"><span class="field-label">Oral Motor Findings:</span><span class="field-value">${findings.oral_motor || ''}</span></div>
              <div class="field"><span class="field-label">Pragmatic / Social Communication:</span><span class="field-value">${findings.pragmatic || ''}</span></div>
            </div>
            
            <div class="section">
              <div class="section-title">ADVICE / PLAN:</div>
              <p>${findings.advice_plan || ''}</p>
            </div>
          `}
          
          <div class="signature">
            <p><strong>Name:</strong> ${findings.therapist_name || report.therapist_name}</p>
            <p><strong>Designation:</strong> ${findings.therapist_designation || ''}</p>
            <p><strong>Organization:</strong> ${findings.therapist_organization || ICRCL_NAME}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const viewReport = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  const openUploadDialog = (report) => {
    setSelectedReport(report);
    setUploadDialogOpen(true);
  };

  const handleDownload = (report) => {
    toast.success(`Downloading ${report.report_type === 'ot_evaluation' ? 'OT' : 'SLT'} Evaluation Report for ${report.patient_name}`);
  };

  const handleEmail = (report) => {
    toast.success(`Email dialog opened - From: ${ICRCL_EMAIL}`);
  };

  const handleSMS = (report) => {
    toast.success(`SMS dialog opened - From: ${ICRCL_PHONE}`);
  };

  const getTypeLabel = (type) => {
    const labels = {
      ot_evaluation: 'OT Evaluation Report',
      slt_evaluation: 'SLT Evaluation Report'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Evaluation Reports</h1>
          <p className="text-slate-500 mt-1">OT & SLT evaluation reports - £70 per report</p>
        </div>
        
        <div className="flex gap-2">
          {/* Create New Template Button */}
          {(user?.role === 'director' || user?.role === 'clinical_lead') && (
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="create-eval-template-btn">
                  <FilePlus className="w-4 h-4 mr-2" />
                  Create Evaluation Form
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Evaluation Report Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Template Name *</Label>
                      <Input 
                        value={templateForm.name} 
                        onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} 
                        placeholder="e.g., Paediatric OT Evaluation"
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select value={templateForm.type} onValueChange={(v) => setTemplateForm({...templateForm, type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ot">Occupational Therapy</SelectItem>
                          <SelectItem value="slt">Speech & Language</SelectItem>
                          <SelectItem value="pt">Physiotherapy</SelectItem>
                          <SelectItem value="psychology">Psychology</SelectItem>
                          <SelectItem value="nutrition">Nutrition</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={templateForm.description} 
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})} 
                      placeholder="Brief description of this evaluation report form..."
                      rows={2}
                    />
                  </div>

                  {/* Sections */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">Report Sections</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addSection}>
                        <Plus className="w-3 h-3 mr-1" />
                        Add Section
                      </Button>
                    </div>

                    {templateForm.sections.map((section, sectionIndex) => (
                      <Card key={sectionIndex} className="p-4 bg-slate-50">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Input 
                              value={section.title}
                              onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                              placeholder="Section Title (e.g., FINDINGS, RECOMMENDATIONS)"
                              className="font-medium"
                            />
                            {templateForm.sections.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeSection(sectionIndex)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                          
                          {/* Fields */}
                          <div className="space-y-2 pl-4">
                            {section.fields.map((field, fieldIndex) => (
                              <div key={fieldIndex} className="flex items-center gap-2">
                                <Input 
                                  value={field.name}
                                  onChange={(e) => updateField(sectionIndex, fieldIndex, 'name', e.target.value)}
                                  placeholder="Field name (e.g., Behaviour, Sensory, Motor Skills)"
                                  className="flex-1"
                                />
                                <Select 
                                  value={field.type} 
                                  onValueChange={(v) => updateField(sectionIndex, fieldIndex, 'type', v)}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="text">Short Text</SelectItem>
                                    <SelectItem value="textarea">Long Text</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                    <SelectItem value="select">Dropdown</SelectItem>
                                    <SelectItem value="checkbox">Checkbox</SelectItem>
                                  </SelectContent>
                                </Select>
                                <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                                  <input 
                                    type="checkbox" 
                                    checked={field.required}
                                    onChange={(e) => updateField(sectionIndex, fieldIndex, 'required', e.target.checked)}
                                  />
                                  Required
                                </label>
                                {section.fields.length > 1 && (
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeField(sectionIndex, fieldIndex)}>
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button type="button" variant="ghost" size="sm" onClick={() => addField(sectionIndex)} className="text-xs">
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#1e3a5f]">Create Template</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* New Evaluation Report Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingReport(null); resetForm(); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="new-evaluation-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Evaluation Report
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">
                {editingReport ? 'Edit Evaluation Report' : 'Create Evaluation Report'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Patient & Report Type Selection */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Patient *</Label>
                  <Select value={formData.patient_id} onValueChange={handlePatientSelect} disabled={!!editingReport}>
                    <SelectTrigger data-testid="eval-patient-select">
                      <SelectValue placeholder="Select patient..." />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Report Type *</Label>
                  <Select value={formData.report_type} onValueChange={handleReportTypeSelect} disabled={!!editingReport}>
                    <SelectTrigger data-testid="eval-type-select">
                      <SelectValue placeholder="Select report type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ot_evaluation">OT Evaluation Report</SelectItem>
                      <SelectItem value="slt_evaluation">SLT Evaluation Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
                </div>
              </div>

              {/* Patient Info Display */}
              {formData.patient_id && (
                <div className="bg-slate-50 p-4 rounded-lg grid grid-cols-5 gap-4 text-sm">
                  <div><strong>Child&apos;s Name:</strong> {formData.child_name}</div>
                  <div><strong>Therapy:</strong> {formData.therapy}</div>
                  <div><strong>DOB:</strong> {formData.dob ? new Date(formData.dob).toLocaleDateString('en-GB') : ''}</div>
                  <div><strong>Sex:</strong> {formData.sex}</div>
                  <div><strong>Date:</strong> {new Date(formData.date).toLocaleDateString('en-GB')}</div>
                </div>
              )}

              {/* Entry Mode Selection - Only for new evaluations */}
              {!editingReport && formData.patient_id && formData.report_type && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-medium text-blue-800 mb-3">How would you like to create this evaluation?</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        entryMode === 'upload' ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-green-300'
                      }`}
                      onClick={() => setEntryMode('upload')}
                    >
                      <div className="flex items-center gap-2">
                        <Upload className={`w-5 h-5 ${entryMode === 'upload' ? 'text-green-600' : 'text-slate-500'}`} />
                        <span className="font-medium">Upload Completed Document</span>
                      </div>
                      <p className="text-xs text-green-600 mt-1">Will be marked as COMPLETE</p>
                    </div>
                    <div 
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        entryMode === 'form' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'
                      }`}
                      onClick={() => setEntryMode('form')}
                    >
                      <div className="flex items-center gap-2">
                        <ClipboardList className={`w-5 h-5 ${entryMode === 'form' ? 'text-blue-600' : 'text-slate-500'}`} />
                        <span className="font-medium">Fill Evaluation Form</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Complete using digital form</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Section - Only show in upload mode */}
              {!editingReport && entryMode === 'upload' && formData.patient_id && formData.report_type && (
                <div className="space-y-4">
                  {!uploadedEvalFile ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 mb-2">Upload your completed evaluation document</p>
                      <p className="text-sm text-slate-400 mb-3">PDF, DOC, DOCX, JPG, PNG (max 20MB)</p>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleEvalFileSelect}
                        className="hidden"
                        id="eval-doc-upload"
                      />
                      <Label htmlFor="eval-doc-upload">
                        <Button type="button" variant="outline" className="cursor-pointer" asChild>
                          <span><Upload className="w-4 h-4 mr-2" />Select Document</span>
                        </Button>
                      </Label>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="font-medium text-green-800">{uploadedEvalFile.name}</p>
                            <p className="text-sm text-green-600">{(uploadedEvalFile.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setUploadedEvalFile(null)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setUploadedEvalFile(null); }}>
                      Cancel
                    </Button>
                    <Button 
                      type="button"
                      onClick={handleUploadEvalSubmit}
                      disabled={!uploadedEvalFile || uploadingEval}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {uploadingEval ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4 mr-2" />Create Complete Evaluation</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* OT Evaluation Form - Only show in form mode */}
              {(editingReport || entryMode === 'form') && formData.report_type === 'ot_evaluation' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Purpose</Label>
                    <Textarea value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} rows={2} />
                  </div>
                  
                  <h4 className="font-bold text-[#1e3a5f] border-b pb-2">FINDINGS</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Behaviour</Label>
                      <Textarea value={formData.behaviour} onChange={(e) => setFormData({...formData, behaviour: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Sensory</Label>
                      <Textarea value={formData.sensory} onChange={(e) => setFormData({...formData, sensory: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Neuromusculoskeletal</Label>
                      <Textarea value={formData.neuromusculoskeletal} onChange={(e) => setFormData({...formData, neuromusculoskeletal: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Reflex</Label>
                      <Textarea value={formData.reflex} onChange={(e) => setFormData({...formData, reflex: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Muscle Strength</Label>
                      <Textarea value={formData.muscle_strength} onChange={(e) => setFormData({...formData, muscle_strength: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Muscle Tone</Label>
                      <Textarea value={formData.muscle_tone} onChange={(e) => setFormData({...formData, muscle_tone: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Gross Motor</Label>
                      <Textarea value={formData.gross_motor} onChange={(e) => setFormData({...formData, gross_motor: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Fine Motor</Label>
                      <Textarea value={formData.fine_motor} onChange={(e) => setFormData({...formData, fine_motor: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Praxis</Label>
                      <Textarea value={formData.praxis} onChange={(e) => setFormData({...formData, praxis: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Cognition and Perception</Label>
                      <Textarea value={formData.cognition_perception} onChange={(e) => setFormData({...formData, cognition_perception: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Academic Skills</Label>
                      <Textarea value={formData.academic_skills} onChange={(e) => setFormData({...formData, academic_skills: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Problem Solving Skills</Label>
                      <Textarea value={formData.problem_solving} onChange={(e) => setFormData({...formData, problem_solving: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Socialization</Label>
                      <Textarea value={formData.socialization} onChange={(e) => setFormData({...formData, socialization: e.target.value})} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Play</Label>
                      <Textarea value={formData.play} onChange={(e) => setFormData({...formData, play: e.target.value})} rows={2} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>ADL Performance</Label>
                    <Textarea value={formData.adl_performance} onChange={(e) => setFormData({...formData, adl_performance: e.target.value})} rows={2} />
                  </div>
                  
                  <h4 className="font-bold text-[#1e3a5f] border-b pb-2">ADVICE / PLAN</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>At Home</Label>
                      <Textarea value={formData.advice_home} onChange={(e) => setFormData({...formData, advice_home: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>At School</Label>
                      <Textarea value={formData.advice_school} onChange={(e) => setFormData({...formData, advice_school: e.target.value})} rows={3} />
                    </div>
                  </div>
                </div>
              )}

              {/* SLT Evaluation Form - Only show in form mode */}
              {(editingReport || entryMode === 'form') && formData.report_type === 'slt_evaluation' && (
                <div className="space-y-4">
                  <h4 className="font-bold text-[#1e3a5f] border-b pb-2">FINDINGS</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Attention and Listening</Label>
                      <Textarea value={formData.attention_listening} onChange={(e) => setFormData({...formData, attention_listening: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>Comprehension (Receptive Language)</Label>
                      <Textarea value={formData.comprehension} onChange={(e) => setFormData({...formData, comprehension: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>Expression (Expressive Language)</Label>
                      <Textarea value={formData.expression} onChange={(e) => setFormData({...formData, expression: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>Speech Sound Production / Articulation</Label>
                      <Textarea value={formData.speech_sound} onChange={(e) => setFormData({...formData, speech_sound: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>Oral Motor Findings</Label>
                      <Textarea value={formData.oral_motor} onChange={(e) => setFormData({...formData, oral_motor: e.target.value})} rows={3} />
                    </div>
                    <div className="space-y-2">
                      <Label>Pragmatic / Social Communication</Label>
                      <Textarea value={formData.pragmatic} onChange={(e) => setFormData({...formData, pragmatic: e.target.value})} rows={3} />
                    </div>
                  </div>
                  
                  <h4 className="font-bold text-[#1e3a5f] border-b pb-2">ADVICE / PLAN</h4>
                  
                  <div className="space-y-2">
                    <Label>Advice / Plan</Label>
                    <Textarea value={formData.advice_plan} onChange={(e) => setFormData({...formData, advice_plan: e.target.value})} rows={4} />
                  </div>
                </div>
              )}

              {/* Therapist Info */}
              {formData.report_type && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-3">Therapist Information</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={formData.therapist_name} onChange={(e) => setFormData({...formData, therapist_name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Designation</Label>
                      <Input value={formData.therapist_designation} onChange={(e) => setFormData({...formData, therapist_designation: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Organization</Label>
                      <Input value={formData.therapist_organization} onChange={(e) => setFormData({...formData, therapist_organization: e.target.value})} />
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingReport(null); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-[#1e3a5f]" disabled={!formData.patient_id || !formData.report_type}>
                  {editingReport ? 'Update Report' : 'Save Report'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Custom Evaluation Templates */}
      {customTemplates.length > 0 && (
        <Card className="card-icrcl">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Custom Evaluation Report Forms</CardTitle>
            <CardDescription>Evaluation templates created by your team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {customTemplates.map((template) => (
                <div key={template.id} className="p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-green-800">{template.name}</p>
                      <p className="text-xs text-green-600">{template.type} | {template.sections?.length || 0} sections</p>
                      <p className="text-xs text-slate-500 mt-1">Created by {template.created_by}</p>
                    </div>
                    {user?.role === 'director' && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports Table */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="text-lg">All Evaluation Reports</CardTitle>
          <CardDescription>OT and SLT evaluation reports</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : evaluations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No evaluation reports yet</p>
              <Button variant="link" className="text-[#2a9d8f]" onClick={() => setDialogOpen(true)}>
                Create your first evaluation report
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Patient</TableHead>
                  <TableHead>Report Type</TableHead>
                  <TableHead>Therapist</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {evaluations.map((report) => (
                  <TableRow key={report.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#4ade80]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <span className="font-medium">{report.patient_name || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={report.report_type === 'ot_evaluation' ? 'border-blue-300 text-blue-700' : 'border-purple-300 text-purple-700'}>
                        {getTypeLabel(report.report_type)}
                      </Badge>
                    </TableCell>
                    <TableCell>{report.therapist_name || 'Unknown'}</TableCell>
                    <TableCell>{new Date(report.date).toLocaleDateString('en-GB')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={(report.status === 'final' || report.has_uploaded_document) ? 'badge-completed' : 'badge-pending'}>
                          {report.has_uploaded_document ? 'COMPLETE' : report.status}
                        </Badge>
                        {report.has_uploaded_document && (
                          <Upload className="w-3 h-3 text-green-600" title="Has uploaded document" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewReport(report)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="evaluation-actions-menu">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Edit - only for owner or super admin */}
                            {canEditReport(report) && (
                              <DropdownMenuItem onClick={() => handleEdit(report)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Report
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handlePrint(report)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Print
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(report)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEmail(report)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSMS(report)}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Send SMS
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openUploadDialog(report)}>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Documents
                            </DropdownMenuItem>
                            {/* Delete - only for super admin */}
                            {isSuperAdmin && (
                              <DropdownMenuItem 
                                onClick={() => { setReportToDelete(report); setDeleteConfirmOpen(true); }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Report
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              {selectedReport?.report_type === 'ot_evaluation' ? 'OT Evaluation Report' : 'SLT Evaluation Report'}
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg grid grid-cols-3 gap-4 text-sm">
                <div><strong>Child&apos;s Name:</strong> {selectedReport.findings?.child_name || selectedReport.patient_name}</div>
                <div><strong>Date:</strong> {new Date(selectedReport.date).toLocaleDateString('en-GB')}</div>
                <div><strong>Therapist:</strong> {selectedReport.therapist_name}</div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-bold text-[#1e3a5f]">Findings</h4>
                {selectedReport.report_type === 'ot_evaluation' ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {['behaviour', 'sensory', 'neuromusculoskeletal', 'gross_motor', 'fine_motor', 'cognition_perception', 'academic_skills', 'socialization', 'play', 'adl_performance'].map(field => (
                      selectedReport.findings?.[field] && (
                        <div key={field} className="p-2 bg-slate-50 rounded">
                          <strong className="capitalize">{field.replace('_', ' ')}:</strong> {selectedReport.findings[field]}
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {['attention_listening', 'comprehension', 'expression', 'speech_sound', 'oral_motor', 'pragmatic'].map(field => (
                      selectedReport.findings?.[field] && (
                        <div key={field} className="p-2 bg-slate-50 rounded">
                          <strong className="capitalize">{field.replace('_', ' ')}:</strong> {selectedReport.findings[field]}
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button onClick={() => handlePrint(selectedReport)} className="bg-[#1e3a5f]">
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evaluation Documents Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              <FolderOpen className="w-5 h-5 inline mr-2" />
              Evaluation Documents - {selectedReport?.patient_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-6">
              {/* Drag & Drop Upload Zone */}
              <DragDropUploader
                entityType="evaluation"
                entityId={selectedReport.id}
                onUploadComplete={() => {}}
                acceptTypes="all"
                title="Upload Evaluation Documents"
                description="Drag & drop external reports, clinical photos, or correspondence"
              />

              {/* Existing Attachments */}
              <AttachmentsList
                entityType="evaluation"
                entityId={selectedReport.id}
                title="Attached Documents"
                showUploadButton={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Evaluation Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="bg-red-50 border-red-200">
              <AlertDescription className="text-red-800">
                <strong>Warning:</strong> This action cannot be undone. The evaluation report will be permanently deleted.
              </AlertDescription>
            </Alert>
            {reportToDelete && (
              <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-1">
                <p><strong>Patient:</strong> {reportToDelete.patient_name}</p>
                <p><strong>Type:</strong> {reportToDelete.report_type === 'ot_evaluation' ? 'OT Evaluation' : 'SLT Evaluation'}</p>
                <p><strong>Date:</strong> {new Date(reportToDelete.date).toLocaleDateString('en-GB')}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteConfirmOpen(false); setReportToDelete(null); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
